/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import { formatDate } from '@squiz/dxp-console-support';
import {
  API_TABLE_EVENT_LOADING,
  API_TABLE_EVENT_PAGE_NEXT,
  API_TABLE_EVENT_PAGE_PREVIOUS,
  ApiTable,
  ApiTableFetchOptions,
  ApiTablePageTokens,
  ApiTableProps,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  Button,
  EmptyState,
  EmptyStateProps,
  ICON_DELETE,
  ICON_SUPERVISED_USER_CIRCLE_WITH_LIST,
  TOOLTIP_POSITION_LEFT,
  TableCell,
  TableContent,
} from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import { useCustomerDelete } from 'apps/customer-data-platform/src/Hooks/UseCustomerDelete/UseCustomerDelete.hook';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import {
  EntityScvCustomer,
  EntityScvCustomers,
  EntityScvCustomersLinks,
  SingleCustomerViewServiceGetCustomersDto,
  getScvService,
} from '../../../../Services/single-customer-view';
import { CUSTOMERS_DATA_IDS } from '../../SegmentInsights.constants';
import { ExportButton } from '../SegmentInsightsView/elements/ExportButton/ExportButton';
import { CUSTOMER_LIST_EMPTY_VALUE } from './CustomerList.constants';
import { CustomerListProps } from './CustomerList.props';

/**
 * View all customers from a chosen segment.
 */
export const CustomerList = (props: CustomerListProps): JSX.Element => {
  const { t } = useTranslation();
  const { activeSegment } = props;
  const apiTableRef = useRef(null);

  // Single Customer View service
  const singleCustomerViewService = getScvService();

  const [customers, setCustomers] = useState<EntityScvCustomers | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const hasProfileAccess = hasPermission(PERMISSIONS.dxpSCV.CUSTOMER_READ);
  const canDeleteCustomer = hasPermission(PERMISSIONS.dxpSCV.CUSTOMER_DELETE);

  // Handle navigation
  const navigate = useNavigate();
  const handleNavigation = useCallback(
    (itemPath: string): void => {
      navigate(itemPath);
    },
    [navigate],
  );

  // Labels
  const labels = useMemo(() => {
    return {
      customerId: t(CDP_STRINGS.customerId),
      delete: t(CDP_STRINGS.delete),
      deleteCustomer: t(CDP_STRINGS.deleteCustomer),
      email: t(CDP_STRINGS.email),
      lastUpdated: t(CDP_STRINGS.lastUpdated),
      name: t(CDP_STRINGS.name),
      noCustomers: t(CDP_STRINGS.noCustomersCaption),
    };
  }, [t]);

  // Extract a page token string from the customer data
  const getPageToken = (links: EntityScvCustomersLinks, type: 'next' | 'prev'): string | undefined => {
    let param: string;

    if (type === 'next') {
      param = encodeURIComponent('page[after]');
    } else {
      param = encodeURIComponent('page[before]');
    }

    const pageComponent = links[type].split('&').filter((item) => item.indexOf(param) !== -1);
    if (pageComponent.length) {
      return pageComponent[0].split(`${param}=`)[1];
    }

    return;
  };

  // Page token object for pagination
  const pageTokens = useMemo(() => {
    const pageTokens: ApiTablePageTokens = {};

    if (customers && customers.links) {
      pageTokens.pageAfter = getPageToken(customers.links, 'next');
      pageTokens.pageBefore = getPageToken(customers.links, 'prev');
    }

    return pageTokens;
  }, [customers]);

  const fetchData = useCallback(
    (options: ApiTableFetchOptions) => {
      const { eventType, pageSize } = options;

      if (activeSegment) {
        setIsLoading(true);

        let pageBefore: string | undefined;
        let pageAfter: string | undefined;

        if (eventType === API_TABLE_EVENT_PAGE_PREVIOUS) {
          pageBefore = pageTokens.pageBefore;
        }

        if (eventType === API_TABLE_EVENT_PAGE_NEXT) {
          pageAfter = pageTokens.pageAfter;
        }

        const dto: SingleCustomerViewServiceGetCustomersDto = {
          pageAfter,
          pageBefore,
          pageSize,
          segment: activeSegment.code,
        };

        lastValueFrom(singleCustomerViewService.getCustomers(dto))
          .then((response) => {
            setCustomers(response as EntityScvCustomers);
            setIsLoading(false);
            return true;
          })
          .catch(() => {
            // Just show regular empty message
            setCustomers({ data: [] });
            setIsLoading(false);
          });
      }
    },
    [activeSegment, pageTokens, setCustomers, setIsLoading, singleCustomerViewService],
  );

  const onDelete = useCallback((): void => {
    fetchData({ eventType: API_TABLE_EVENT_LOADING, ...pageTokens } as ApiTableFetchOptions);
  }, [fetchData, pageTokens]);

  const { openDeleteCustomerModal } = useCustomerDelete({
    onDelete,
  });

  const tableColumns: Array<TableCell> = useMemo((): Array<TableCell> => {
    const cols = [
      {
        content: labels.customerId,
        key: `customerId`,
      },
      {
        content: labels.name,
        key: `name`,
      },
      {
        content: labels.email,
        key: `email`,
      },
      {
        content: labels.lastUpdated,
        key: `lastUpdated`,
      },
    ];

    if (canDeleteCustomer) {
      cols.push({
        content: ``,
        key: `delete`,
      });
    }
    return cols;
  }, [canDeleteCustomer, labels.customerId, labels.email, labels.lastUpdated, labels.name]);

  const tableRows = useMemo(() => {
    if (!customers) {
      return [];
    }

    return customers.data.map((customer: EntityScvCustomer) => {
      const { $customerid, $updatedDatetime, $verifiedEmail, firstName, lastName } = customer;

      let fullName = CUSTOMER_LIST_EMPTY_VALUE;

      if (firstName && lastName) {
        fullName = `${firstName} ${lastName}`;
      } else if (firstName) {
        fullName = firstName;
      } else if (lastName) {
        fullName = lastName;
      }

      const goToCustomerProfile = (event: React.MouseEvent): void => {
        event.preventDefault();
        const customerProfilePath = getCapabilityPath(`/customers/${$customerid}`);
        handleNavigation(customerProfilePath);
      };

      const customerLink = hasProfileAccess ? (
        <button
          className="customer-list__id customer-list__link"
          data-id={CUSTOMERS_DATA_IDS.customerListLink}
          onClick={goToCustomerProfile}
        >
          {$customerid}
        </button>
      ) : (
        <span className="customer-list__id">{$customerid}</span>
      );

      return {
        customerId: customerLink,
        delete: (
          <Button
            format={BUTTON_FORMAT_TRANSPARENT}
            icon={ICON_DELETE}
            label={labels.delete}
            onClick={(): void => {
              openDeleteCustomerModal($customerid, fullName);
            }}
            theme={BUTTON_THEME_DEFAULT}
            tooltip={{
              label: labels.deleteCustomer,
              position: TOOLTIP_POSITION_LEFT,
            }}
          />
        ),
        email: !$verifiedEmail || /^\s*$/.test($verifiedEmail) ? CUSTOMER_LIST_EMPTY_VALUE : $verifiedEmail,
        lastUpdated: $updatedDatetime ? formatDate(`${$updatedDatetime}`) : CUSTOMER_LIST_EMPTY_VALUE,
        name: fullName,
      };
    });
  }, [customers, handleNavigation, hasProfileAccess, labels.delete, labels.deleteCustomer, openDeleteCustomerModal]);

  // Only show the empty state if we have no data for the current page,
  // and if there are no valid next/previous links.
  const noCustomers = customers && customers.data.length === 0 && !pageTokens.pageAfter && !pageTokens.pageBefore;
  if (noCustomers) {
    const emptyStateProps: EmptyStateProps = {
      caption: labels.noCustomers,
      dataId: CUSTOMERS_DATA_IDS.customerListEmptyState,
      svg: ICON_SUPERVISED_USER_CIRCLE_WITH_LIST,
    };

    return (
      <div className="customer-list__wrapper">
        <EmptyState {...emptyStateProps} />
      </div>
    );
  }

  const tableContent: TableContent = { columns: tableColumns, rows: tableRows };
  const tableProps: ApiTableProps = {
    fetchData,
    loading: isLoading,
    pageTokens,
    perPageDefault: 20,
    tableContent: tableContent,
  };

  return (
    <div className="customer-list__wrapper">
      {activeSegment && customers && <ExportButton segment={activeSegment.code} />}
      <ApiTable {...tableProps} ref={apiTableRef} />
    </div>
  );
};
